import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';

const CognitoCallback: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { createSession } = useAuth();
  const hasExecuted = useRef(false);

  const handleCallback = async () => {
    try {
      setLoading(true);
      setError('');

      // Get parameters from URL
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      // Check for OAuth error
      if (errorParam) {
        throw new Error(`OAuth error: ${errorParam}`);
      }

      // Validate required parameters
      if (!code || !state) {
        throw new Error('Missing authorization code or state parameter');
      }

      // Validate state parameter (CSRF protection)
      const storedState = localStorage.getItem('cognito_sso_state');
      console.log('State validation:', { received: state, stored: storedState, match: state === storedState });
      if (state !== storedState) {
        throw new Error(`Invalid state parameter. Received: ${state}, Stored: ${storedState}`);
      }

      // Clear stored state
      localStorage.removeItem('cognito_sso_state');

      // Exchange code for tokens via SyberKey service
      // API Service - use production URL by default, localhost for development
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 
        (window.location.hostname === 'sso.syberkey.com' || !window.location.hostname.includes('localhost')
         ? 'https://api.syberkey.com' 
         : 'http://localhost:3000');
      
      // Determine redirect URI based on environment
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const redirectUri = isLocalhost 
        ? `${window.location.protocol}//${window.location.host}/callback`
        : `https://sso.syberkey.com/callback`;
      
      // Use syberkey_sso tenant ID
      const tenantId = 'syberkey_sso_64a4c5';
      
      console.log('[CognitoCallback] Exchanging code for tokens:', {
        code: code.substring(0, 10) + '...',
        redirect_uri: redirectUri,
        tenant_id: tenantId,
        api_base_url: apiBaseUrl
      });

      const tokenResponse = await fetch(`${apiBaseUrl}/cognito-sso/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: code,
          state: state,
          redirect_uri: redirectUri,
          tenant_id: tenantId
        })
      });

      // Check if response is JSON before parsing
      const contentType = tokenResponse.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await tokenResponse.text();
        console.error('[CognitoCallback] Non-JSON response received:', {
          status: tokenResponse.status,
          contentType: contentType,
          preview: text.substring(0, 200)
        });
        throw new Error(`Server returned non-JSON response (${tokenResponse.status}). This might be a routing or endpoint issue.`);
      }

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        console.error('[CognitoCallback] Token exchange failed:', { 
          status: tokenResponse.status, 
          error: errorData 
        });
        throw new Error(errorData.message || `Token exchange failed: ${tokenResponse.status} ${tokenResponse.statusText}`);
      }

      const tokenData = await tokenResponse.json();
      console.log('[CognitoCallback] Token exchange successful:', {
        has_tokens: !!tokenData.cognito_tokens,
        has_user: !!tokenData.user,
        has_token: !!tokenData.token
      });

      // Store Cognito tokens and user data
      if (tokenData.cognito_tokens) {
        localStorage.setItem('cognito_access_token', tokenData.cognito_tokens.access_token);
        localStorage.setItem('cognito_id_token', tokenData.cognito_tokens.id_token);
        if (tokenData.cognito_tokens.refresh_token) {
          localStorage.setItem('cognito_refresh_token', tokenData.cognito_tokens.refresh_token);
        }
      }

      // Standardize response: Use same format as OIDC callback
      // Create SSO session via AuthContext (similar to OIDC flow)
      if (tokenData.user) {
        // Store user data temporarily
        localStorage.setItem('cognito_user_email', tokenData.user.email);
        localStorage.setItem('cognito_user_name', tokenData.user.fullName || tokenData.user.name);
        localStorage.setItem('cognito_user_id', tokenData.user.userId);
        
        // Create SSO session using AuthContext (standardized with OIDC)
        try {
          console.log('[CognitoCallback] Creating SSO session via AuthContext...');
          await createSession({
            userId: tokenData.user.userId,
            email: tokenData.user.email,
            fullName: tokenData.user.fullName || tokenData.user.name,
            tenantId: tenantId,
            loginId: tokenData.user.userId // Use userId as loginId
          });

          console.log('[CognitoCallback] SSO session created successfully');
          // Redirect to dashboard (standardized with OIDC)
          navigate('/dashboard', { replace: true });
          return;
        } catch (sessionError) {
          console.error('[CognitoCallback] Failed to create SSO session:', sessionError);
          // Fall through to MFA flow if session creation fails
        }
      }

      // Fallback: Redirect to SyberKey MFA flow (if session creation failed or not available)
      const params = new URLSearchParams({
        email: tokenData.user?.email || localStorage.getItem('cognito_user_email') || '',
        name: tokenData.user?.fullName || tokenData.user?.name || localStorage.getItem('cognito_user_name') || '',
        userId: tokenData.user?.userId || tokenData.user?.email || ''
      });
      
      navigate(`/syberkey-mfa?${params.toString()}`);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (!hasExecuted.current) {
      hasExecuted.current = true;
      handleCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-content">
            <div className="loading-spinner"></div>
            <h2>Completing Authentication...</h2>
            <p>Please wait while we complete your login.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-content">
            <div className="error-message">
              {error}
            </div>
            <button 
              className="syberkey-login-btn"
              onClick={() => navigate('/')}
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CognitoCallback;
